import React from 'react';

export type PageAtmosphereVariant =
	| 'home'
	| 'about'
	| 'careers'
	| 'blog'
	| 'privacy'
	| 'terms'
	| 'cookies'
	| 'index-company'
	| 'index-work'
	| 'index-lab'
	| 'index-kairos'
	| 'index-contact';

interface PageAtmosphereProps {
	variant: PageAtmosphereVariant;
}

export const PageAtmosphere: React.FC<PageAtmosphereProps> = ({ variant }) => {
	if (variant === 'home') {
		return (
			<div className="page-atmosphere page-atmosphere--home" aria-hidden="true">
				<svg viewBox="0 0 560 440" role="presentation">
					<defs>
						<linearGradient id="home-ellipse-fade-cream" gradientUnits="userSpaceOnUse" x1="30" y1="112" x2="530" y2="330">
							<stop offset="0" stopColor="#eeeae0" stopOpacity="0" />
							<stop offset="0.24" stopColor="#eeeae0" stopOpacity="0.72" />
							<stop offset="0.54" stopColor="#a7b75a" stopOpacity="0.92" />
							<stop offset="0.82" stopColor="#667327" stopOpacity="0.44" />
							<stop offset="1" stopColor="#667327" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="home-ellipse-fade-olive" gradientUnits="userSpaceOnUse" x1="470" y1="66" x2="96" y2="378">
							<stop offset="0" stopColor="#667327" stopOpacity="0" />
							<stop offset="0.27" stopColor="#8d9d43" stopOpacity="0.82" />
							<stop offset="0.62" stopColor="#eeeae0" stopOpacity="0.6" />
							<stop offset="1" stopColor="#eeeae0" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="home-ellipse-fade-deep" gradientUnits="userSpaceOnUse" x1="150" y1="390" x2="404" y2="42">
							<stop offset="0" stopColor="#eeeae0" stopOpacity="0" />
							<stop offset="0.3" stopColor="#eeeae0" stopOpacity="0.48" />
							<stop offset="0.68" stopColor="#72802f" stopOpacity="0.86" />
							<stop offset="1" stopColor="#667327" stopOpacity="0" />
						</linearGradient>
						<radialGradient id="home-ellipse-lens" cx="46%" cy="42%" r="68%">
							<stop offset="0" stopColor="#a7b75a" stopOpacity="0.2" />
							<stop offset="0.48" stopColor="#667327" stopOpacity="0.08" />
							<stop offset="1" stopColor="#070806" stopOpacity="0" />
						</radialGradient>
					</defs>
					<ellipse className="home-ellipse-lens" cx="280" cy="220" rx="205" ry="82" />
					<g className="home-ellipse-system home-ellipse-system--outer">
						<path id="home-ellipse-outer" className="home-ellipse-line" stroke="url(#home-ellipse-fade-cream)" d="M30 220A250 98 0 1 0 530 220A250 98 0 1 0 30 220" />
						<use className="home-ellipse-sweep home-ellipse-sweep--outer" href="#home-ellipse-outer" />
						<circle className="home-ellipse-dot" r="7">
							<animateMotion dur="17s" begin="-8s" repeatCount="indefinite">
								<mpath href="#home-ellipse-outer" />
							</animateMotion>
						</circle>
					</g>
					<g className="home-ellipse-system home-ellipse-system--middle">
						<path id="home-ellipse-middle" className="home-ellipse-line home-ellipse-line--middle" stroke="url(#home-ellipse-fade-olive)" d="M80 220A200 66 0 1 0 480 220A200 66 0 1 0 80 220" />
						<circle className="home-ellipse-dot home-ellipse-dot--paper" r="5">
							<animateMotion dur="8.5s" begin="-3s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
								<mpath href="#home-ellipse-middle" />
							</animateMotion>
						</circle>
					</g>
					<g className="home-ellipse-system home-ellipse-system--vertical">
						<path id="home-ellipse-vertical" className="home-ellipse-line home-ellipse-line--vertical" stroke="url(#home-ellipse-fade-deep)" d="M172 220A108 178 0 1 0 388 220A108 178 0 1 0 172 220" />
						<use className="home-ellipse-sweep home-ellipse-sweep--vertical" href="#home-ellipse-vertical" />
						<circle className="home-ellipse-dot home-ellipse-dot--small" r="4">
							<animateMotion dur="11s" begin="-5s" repeatCount="indefinite">
								<mpath href="#home-ellipse-vertical" />
							</animateMotion>
						</circle>
					</g>
					<g className="home-ellipse-system home-ellipse-system--micro-wide">
						<path id="home-ellipse-micro-wide" className="home-ellipse-line home-ellipse-line--micro" stroke="url(#home-ellipse-fade-olive)" d="M202 220A78 30 0 1 0 358 220A78 30 0 1 0 202 220" />
						<circle className="home-ellipse-dot home-ellipse-dot--micro" r="3.25">
							<animateMotion dur="9.4s" begin="-6s" repeatCount="indefinite">
								<mpath href="#home-ellipse-micro-wide" />
							</animateMotion>
						</circle>
					</g>
					<g className="home-ellipse-system home-ellipse-system--micro-round">
						<path id="home-ellipse-micro-round" className="home-ellipse-line home-ellipse-line--micro home-ellipse-line--micro-round" stroke="url(#home-ellipse-fade-cream)" d="M234 220A46 46 0 1 0 326 220A46 46 0 1 0 234 220" />
						<circle className="home-ellipse-dot home-ellipse-dot--micro home-ellipse-dot--micro-paper" r="2.75">
							<animateMotion dur="5.8s" begin="-2s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
								<mpath href="#home-ellipse-micro-round" />
							</animateMotion>
						</circle>
					</g>
					<circle className="home-ellipse-core-glow" cx="280" cy="220" r="28" />
					<circle className="home-ellipse-core-pulse" cx="280" cy="220" r="19" />
					<rect className="home-ellipse-core" x="272" y="212" width="16" height="16" />
				</svg>
			</div>
		);
	}

	if (variant === 'about') {
		return (
			<div className="page-atmosphere page-atmosphere--about" aria-hidden="true">
				<svg viewBox="0 0 540 480" role="presentation">
					<defs>
						<linearGradient id="about-ellipse-fade-ivory" gradientUnits="userSpaceOnUse" x1="40" y1="90" x2="500" y2="390">
							<stop offset="0" stopColor="#f3efe6" stopOpacity="0" />
							<stop offset="0.22" stopColor="#f3efe6" stopOpacity="0.75" />
							<stop offset="0.55" stopColor="#b4c657" stopOpacity="0.9" />
							<stop offset="0.85" stopColor="#5f6d21" stopOpacity="0.45" />
							<stop offset="1" stopColor="#5f6d21" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="about-ellipse-fade-chartreuse" gradientUnits="userSpaceOnUse" x1="480" y1="70" x2="80" y2="410">
							<stop offset="0" stopColor="#5f6d21" stopOpacity="0" />
							<stop offset="0.26" stopColor="#96a841" stopOpacity="0.85" />
							<stop offset="0.65" stopColor="#f3efe6" stopOpacity="0.62" />
							<stop offset="1" stopColor="#f3efe6" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="about-ellipse-fade-slate" gradientUnits="userSpaceOnUse" x1="160" y1="440" x2="380" y2="40">
							<stop offset="0" stopColor="#f3efe6" stopOpacity="0" />
							<stop offset="0.32" stopColor="#f3efe6" stopOpacity="0.5" />
							<stop offset="0.7" stopColor="#7a8b30" stopOpacity="0.88" />
							<stop offset="1" stopColor="#5f6d21" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="about-ellipse-fade-amber" gradientUnits="userSpaceOnUse" x1="90" y1="240" x2="450" y2="240">
							<stop offset="0" stopColor="#7a8b30" stopOpacity="0" />
							<stop offset="0.35" stopColor="#c3d564" stopOpacity="0.78" />
							<stop offset="0.75" stopColor="#f3efe6" stopOpacity="0.58" />
							<stop offset="1" stopColor="#5f6d21" stopOpacity="0" />
						</linearGradient>
						<radialGradient id="about-ellipse-lens" cx="48%" cy="44%" r="65%">
							<stop offset="0" stopColor="#b4c657" stopOpacity="0.22" />
							<stop offset="0.45" stopColor="#5f6d21" stopOpacity="0.09" />
							<stop offset="1" stopColor="#080907" stopOpacity="0" />
						</radialGradient>
					</defs>

					<ellipse className="about-ellipse-lens" cx="270" cy="240" rx="195" ry="110" />

					<g className="about-ellipse-system about-ellipse-system--primary">
						<path id="about-ellipse-primary" className="about-ellipse-line about-ellipse-line--primary" stroke="url(#about-ellipse-fade-ivory)" d="M35 240A235 95 0 1 0 505 240A235 95 0 1 0 35 240" />
						<use className="about-ellipse-sweep about-ellipse-sweep--primary" href="#about-ellipse-primary" />
						<circle className="about-ellipse-dot about-ellipse-dot--large" r="7.5">
							<animateMotion dur="21s" begin="-9s" repeatCount="indefinite">
								<mpath href="#about-ellipse-primary" />
							</animateMotion>
						</circle>
					</g>

					<g className="about-ellipse-system about-ellipse-system--secondary">
						<path id="about-ellipse-secondary" className="about-ellipse-line about-ellipse-line--secondary" stroke="url(#about-ellipse-fade-chartreuse)" d="M85 240A185 72 0 1 0 455 240A185 72 0 1 0 85 240" />
						<circle className="about-ellipse-dot about-ellipse-dot--medium" r="5.5">
							<animateMotion dur="13.5s" begin="-4s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
								<mpath href="#about-ellipse-secondary" />
							</animateMotion>
						</circle>
					</g>

					<g className="about-ellipse-system about-ellipse-system--steep">
						<path id="about-ellipse-steep" className="about-ellipse-line about-ellipse-line--steep" stroke="url(#about-ellipse-fade-slate)" d="M165 240A105 185 0 1 0 375 240A105 185 0 1 0 165 240" />
						<use className="about-ellipse-sweep about-ellipse-sweep--steep" href="#about-ellipse-steep" />
						<circle className="about-ellipse-dot about-ellipse-dot--small" r="4.5">
							<animateMotion dur="15s" begin="-6s" repeatCount="indefinite">
								<mpath href="#about-ellipse-steep" />
							</animateMotion>
						</circle>
					</g>

					<g className="about-ellipse-system about-ellipse-system--transverse">
						<path id="about-ellipse-transverse" className="about-ellipse-line about-ellipse-line--transverse" stroke="url(#about-ellipse-fade-amber)" d="M145 240A125 44 0 1 0 395 240A125 44 0 1 0 145 240" />
						<circle className="about-ellipse-dot about-ellipse-dot--paper" r="3.5">
							<animateMotion dur="8.2s" begin="-2.5s" repeatCount="indefinite">
								<mpath href="#about-ellipse-transverse" />
							</animateMotion>
						</circle>
					</g>

					<g className="about-ellipse-system about-ellipse-system--micro">
						<path id="about-ellipse-micro" className="about-ellipse-line about-ellipse-line--micro" stroke="url(#about-ellipse-fade-chartreuse)" d="M218 240A52 52 0 1 0 322 240A52 52 0 1 0 218 240" />
						<circle className="about-ellipse-dot about-ellipse-dot--micro" r="2.5">
							<animateMotion dur="5.6s" begin="-1s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
								<mpath href="#about-ellipse-micro" />
							</animateMotion>
						</circle>
					</g>

					<circle className="about-ellipse-core-glow" cx="270" cy="240" r="32" />
					<circle className="about-ellipse-core-pulse" cx="270" cy="240" r="22" />
					<circle className="about-ellipse-core-pulse about-ellipse-core-pulse--wide" cx="270" cy="240" r="14" />
					<polygon className="about-ellipse-core" points="270,228 280,240 270,252 260,240" />
				</svg>
			</div>
		);
	}

	if (variant === 'careers') {
		return (
			<div className="page-atmosphere page-atmosphere--careers" aria-hidden="true">
				<svg viewBox="0 0 540 460" role="presentation">
					<defs>
						<linearGradient id="career-vector-fade-ivory" gradientUnits="userSpaceOnUse" x1="40" y1="360" x2="480" y2="60">
							<stop offset="0" stopColor="#f3efe6" stopOpacity="0" />
							<stop offset="0.22" stopColor="#f3efe6" stopOpacity="0.75" />
							<stop offset="0.55" stopColor="#b4c657" stopOpacity="0.9" />
							<stop offset="0.85" stopColor="#5f6d21" stopOpacity="0.45" />
							<stop offset="1" stopColor="#5f6d21" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="career-vector-fade-chartreuse" gradientUnits="userSpaceOnUse" x1="480" y1="80" x2="40" y2="360">
							<stop offset="0" stopColor="#5f6d21" stopOpacity="0" />
							<stop offset="0.26" stopColor="#96a841" stopOpacity="0.85" />
							<stop offset="0.65" stopColor="#f3efe6" stopOpacity="0.62" />
							<stop offset="1" stopColor="#f3efe6" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="career-vector-fade-slate" gradientUnits="userSpaceOnUse" x1="120" y1="320" x2="480" y2="60">
							<stop offset="0" stopColor="#f3efe6" stopOpacity="0" />
							<stop offset="0.32" stopColor="#f3efe6" stopOpacity="0.5" />
							<stop offset="0.7" stopColor="#7a8b30" stopOpacity="0.88" />
							<stop offset="1" stopColor="#5f6d21" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="career-vector-fade-amber" gradientUnits="userSpaceOnUse" x1="180" y1="320" x2="380" y2="200">
							<stop offset="0" stopColor="#7a8b30" stopOpacity="0" />
							<stop offset="0.35" stopColor="#c3d564" stopOpacity="0.78" />
							<stop offset="0.75" stopColor="#f3efe6" stopOpacity="0.58" />
							<stop offset="1" stopColor="#5f6d21" stopOpacity="0" />
						</linearGradient>
						<radialGradient id="career-ellipse-lens" cx="50%" cy="48%" r="65%">
							<stop offset="0" stopColor="#b4c657" stopOpacity="0.22" />
							<stop offset="0.45" stopColor="#5f6d21" stopOpacity="0.09" />
							<stop offset="1" stopColor="#080907" stopOpacity="0" />
						</radialGradient>
					</defs>

					<ellipse className="career-ellipse-lens" cx="270" cy="230" rx="205" ry="120" />

					{/* Primary Ascending Route Vector System */}
					<g className="career-vector-system career-vector-system--primary">
						<path id="career-route-primary" className="career-vector-line career-vector-line--primary" stroke="url(#career-vector-fade-ivory)" d="M48 346L132 294L203 308L282 205L355 220L466 66" />
						<use className="career-vector-sweep career-vector-sweep--primary" href="#career-route-primary" />
						<circle className="career-vector-dot career-vector-dot--large" r="7.5">
							<animateMotion dur="14s" begin="-5s" repeatCount="indefinite">
								<mpath href="#career-route-primary" />
							</animateMotion>
						</circle>
					</g>

					{/* Harmonic Ascending Curve System */}
					<g className="career-vector-system career-vector-system--secondary">
						<path id="career-route-harmonic" className="career-vector-line career-vector-line--secondary" stroke="url(#career-vector-fade-chartreuse)" d="M48 346 Q160 380 282 205 T466 66" />
						<circle className="career-vector-dot career-vector-dot--medium" r="5.5">
							<animateMotion dur="10.5s" begin="-2s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
								<mpath href="#career-route-harmonic" />
							</animateMotion>
						</circle>
					</g>

					{/* Steep Ridge Ascent Vector System */}
					<g className="career-vector-system career-vector-system--steep">
						<path id="career-route-steep" className="career-vector-line career-vector-line--steep" stroke="url(#career-vector-fade-slate)" d="M132 294 Q240 120 466 66" />
						<use className="career-vector-sweep career-vector-sweep--steep" href="#career-route-steep" />
						<circle className="career-vector-dot career-vector-dot--small" r="4.5">
							<animateMotion dur="12s" begin="-4s" repeatCount="indefinite">
								<mpath href="#career-route-steep" />
							</animateMotion>
						</circle>
					</g>

					{/* Transverse Horizon Calibration Vector System */}
					<g className="career-vector-system career-vector-system--transverse">
						<path id="career-route-transverse" className="career-vector-line career-vector-line--transverse" stroke="url(#career-vector-fade-amber)" d="M96 260 Q270 240 440 260" />
						<circle className="career-vector-dot career-vector-dot--paper" r="3.5">
							<animateMotion dur="8s" begin="-2.5s" repeatCount="indefinite">
								<mpath href="#career-route-transverse" />
							</animateMotion>
						</circle>
					</g>

					{/* Micro Segment Vector System */}
					<g className="career-vector-system career-vector-system--micro">
						<path id="career-route-micro" className="career-vector-line career-vector-line--micro" stroke="url(#career-vector-fade-chartreuse)" d="M203 308 Q282 170 355 220" />
						<circle className="career-vector-dot career-vector-dot--micro" r="2.5">
							<animateMotion dur="5.5s" begin="-1s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
								<mpath href="#career-route-micro" />
							</animateMotion>
						</circle>
					</g>

					{/* Waypoint Node Pivots */}
					<circle className="career-node-pivot" cx="48" cy="346" r="3.5" />
					<circle className="career-node-pivot" cx="132" cy="294" r="3.5" />
					<circle className="career-node-pivot" cx="203" cy="308" r="3.5" />
					<circle className="career-node-pivot" cx="282" cy="205" r="3.5" />
					<circle className="career-node-pivot" cx="355" cy="220" r="3.5" />

					{/* Apex Zenith Core Assembly */}
					<circle className="career-core-glow" cx="466" cy="66" r="28" />
					<circle className="career-core-pulse" cx="466" cy="66" r="18" />
					<circle className="career-core-pulse career-core-pulse--wide" cx="466" cy="66" r="12" />
					<polygon className="career-core" points="466,54 476,66 466,78 456,66" />
				</svg>
			</div>
		);
	}

	if (variant === 'blog') {
		return (
			<div className="page-atmosphere page-atmosphere--blog" aria-hidden="true">
				<svg viewBox="0 0 560 360" role="presentation">
					<path className="blog-ribbon-ghost" d="M16 226C104 80 188 318 282 166C374 17 431 259 544 80" />
					<path className="blog-ribbon-live" d="M16 226C104 80 188 318 282 166C374 17 431 259 544 80" />
				</svg>
			</div>
		);
	}

	if (variant === 'privacy') {
		return (
			<div className="page-atmosphere page-atmosphere--privacy" aria-hidden="true">
				<svg viewBox="0 0 520 460" role="presentation">
					<defs>
						<linearGradient id="privacy-ellipse-fade-ivory" gradientUnits="userSpaceOnUse" x1="40" y1="80" x2="480" y2="380">
							<stop offset="0" stopColor="#f3efe6" stopOpacity="0" />
							<stop offset="0.22" stopColor="#f3efe6" stopOpacity="0.75" />
							<stop offset="0.55" stopColor="#b4c657" stopOpacity="0.9" />
							<stop offset="0.85" stopColor="#5f6d21" stopOpacity="0.45" />
							<stop offset="1" stopColor="#5f6d21" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="privacy-ellipse-fade-chartreuse" gradientUnits="userSpaceOnUse" x1="470" y1="80" x2="60" y2="390">
							<stop offset="0" stopColor="#5f6d21" stopOpacity="0" />
							<stop offset="0.26" stopColor="#96a841" stopOpacity="0.85" />
							<stop offset="0.65" stopColor="#f3efe6" stopOpacity="0.65" />
							<stop offset="1" stopColor="#f3efe6" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="privacy-ellipse-fade-amber" gradientUnits="userSpaceOnUse" x1="100" y1="420" x2="420" y2="50">
							<stop offset="0" stopColor="#f3efe6" stopOpacity="0" />
							<stop offset="0.3" stopColor="#c3d564" stopOpacity="0.78" />
							<stop offset="0.72" stopColor="#7a8b30" stopOpacity="0.88" />
							<stop offset="1" stopColor="#5f6d21" stopOpacity="0" />
						</linearGradient>
						<radialGradient id="privacy-ellipse-lens" cx="48%" cy="45%" r="62%">
							<stop offset="0" stopColor="#b4c657" stopOpacity="0.22" />
							<stop offset="0.45" stopColor="#5f6d21" stopOpacity="0.08" />
							<stop offset="1" stopColor="#080907" stopOpacity="0" />
						</radialGradient>
					</defs>

					<ellipse className="privacy-ellipse-lens" cx="260" cy="230" rx="190" ry="105" />

					<g className="privacy-ellipse-system privacy-ellipse-system--primary">
						<path id="privacy-ellipse-primary" className="privacy-ellipse-line privacy-ellipse-line--primary" stroke="url(#privacy-ellipse-fade-ivory)" d="M40 230A220 94 0 1 0 480 230A220 94 0 1 0 40 230" />
						<use className="privacy-ellipse-sweep privacy-ellipse-sweep--primary" href="#privacy-ellipse-primary" />
						<circle className="privacy-ellipse-dot privacy-ellipse-dot--large" r="7.2">
							<animateMotion dur="18s" begin="-8s" repeatCount="indefinite">
								<mpath href="#privacy-ellipse-primary" />
							</animateMotion>
						</circle>
					</g>

					<g className="privacy-ellipse-system privacy-ellipse-system--meridian">
						<path id="privacy-ellipse-meridian" className="privacy-ellipse-line privacy-ellipse-line--meridian" stroke="url(#privacy-ellipse-fade-chartreuse)" d="M170 230A90 175 0 1 0 350 230A90 175 0 1 0 170 230" />
						<use className="privacy-ellipse-sweep privacy-ellipse-sweep--steep" href="#privacy-ellipse-meridian" />
						<circle className="privacy-ellipse-dot privacy-ellipse-dot--medium" r="5.2">
							<animateMotion dur="13s" begin="-4s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
								<mpath href="#privacy-ellipse-meridian" />
							</animateMotion>
						</circle>
					</g>

					<g className="privacy-ellipse-system privacy-ellipse-system--transverse">
						<path id="privacy-ellipse-transverse" className="privacy-ellipse-line privacy-ellipse-line--transverse" stroke="url(#privacy-ellipse-fade-amber)" d="M90 230A170 70 0 1 0 430 230A170 70 0 1 0 90 230" />
						<circle className="privacy-ellipse-dot privacy-ellipse-dot--small" r="4.2">
							<animateMotion dur="15s" begin="-6s" repeatCount="indefinite">
								<mpath href="#privacy-ellipse-transverse" />
							</animateMotion>
						</circle>
					</g>

					<g className="privacy-ellipse-system privacy-ellipse-system--enclave">
						<path id="privacy-ellipse-enclave" className="privacy-ellipse-line privacy-ellipse-line--enclave" stroke="url(#privacy-ellipse-fade-ivory)" d="M145 230A115 46 0 1 0 375 230A115 46 0 1 0 145 230" />
						<circle className="privacy-ellipse-dot privacy-ellipse-dot--paper" r="3.2">
							<animateMotion dur="8s" begin="-2s" repeatCount="indefinite">
								<mpath href="#privacy-ellipse-enclave" />
							</animateMotion>
						</circle>
					</g>

					<g className="privacy-ellipse-system privacy-ellipse-system--micro">
						<path id="privacy-ellipse-micro" className="privacy-ellipse-line privacy-ellipse-line--micro" stroke="url(#privacy-ellipse-fade-chartreuse)" d="M214 230A46 46 0 1 0 306 230A46 46 0 1 0 214 230" />
						<circle className="privacy-ellipse-dot privacy-ellipse-dot--micro" r="2.5">
							<animateMotion dur="5.4s" begin="-1s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
								<mpath href="#privacy-ellipse-micro" />
							</animateMotion>
						</circle>
					</g>

					<circle className="privacy-ellipse-core-glow" cx="260" cy="230" r="30" />
					<circle className="privacy-ellipse-core-pulse" cx="260" cy="230" r="20" />
					<circle className="privacy-ellipse-core-pulse privacy-ellipse-core-pulse--wide" cx="260" cy="230" r="13" />
					<polygon className="privacy-ellipse-core" points="260,218 270,230 260,242 250,230" />
				</svg>
			</div>
		);
	}

	if (variant === 'terms') {
		return (
			<div className="page-atmosphere page-atmosphere--terms" aria-hidden="true">
				<svg viewBox="0 0 540 420" role="presentation">
					<defs>
						<linearGradient id="terms-ellipse-fade-ivory" gradientUnits="userSpaceOnUse" x1="30" y1="90" x2="510" y2="330">
							<stop offset="0" stopColor="#f3efe6" stopOpacity="0" />
							<stop offset="0.25" stopColor="#f3efe6" stopOpacity="0.75" />
							<stop offset="0.55" stopColor="#b4c657" stopOpacity="0.88" />
							<stop offset="0.85" stopColor="#5f6d21" stopOpacity="0.45" />
							<stop offset="1" stopColor="#5f6d21" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="terms-ellipse-fade-node-left" gradientUnits="userSpaceOnUse" x1="50" y1="130" x2="260" y2="290">
							<stop offset="0" stopColor="#5f6d21" stopOpacity="0" />
							<stop offset="0.28" stopColor="#96a841" stopOpacity="0.85" />
							<stop offset="0.68" stopColor="#f3efe6" stopOpacity="0.65" />
							<stop offset="1" stopColor="#f3efe6" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="terms-ellipse-fade-node-right" gradientUnits="userSpaceOnUse" x1="490" y1="130" x2="280" y2="290">
							<stop offset="0" stopColor="#5f6d21" stopOpacity="0" />
							<stop offset="0.28" stopColor="#96a841" stopOpacity="0.85" />
							<stop offset="0.68" stopColor="#f3efe6" stopOpacity="0.65" />
							<stop offset="1" stopColor="#f3efe6" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="terms-ellipse-fade-meridian" gradientUnits="userSpaceOnUse" x1="270" y1="50" x2="270" y2="370">
							<stop offset="0" stopColor="#f3efe6" stopOpacity="0" />
							<stop offset="0.3" stopColor="#f3efe6" stopOpacity="0.6" />
							<stop offset="0.7" stopColor="#7a8b30" stopOpacity="0.85" />
							<stop offset="1" stopColor="#5f6d21" stopOpacity="0" />
						</linearGradient>
						<radialGradient id="terms-ellipse-lens" cx="50%" cy="50%" r="50%">
							<stop offset="0" stopColor="#b4c657" stopOpacity="0.22" />
							<stop offset="0.5" stopColor="#5f6d21" stopOpacity="0.08" />
							<stop offset="1" stopColor="#080907" stopOpacity="0" />
						</radialGradient>
					</defs>

					<ellipse className="terms-ellipse-lens terms-ellipse-lens--left" cx="155" cy="210" rx="90" ry="60" />
					<ellipse className="terms-ellipse-lens terms-ellipse-lens--right" cx="385" cy="210" rx="90" ry="60" />

					<g className="terms-equilibrium-assembly">
						<path className="terms-ellipse-axis" d="M60 210H480" />

						<g className="terms-ellipse-system terms-ellipse-system--grand">
							<path id="terms-ellipse-grand" className="terms-ellipse-line terms-ellipse-line--grand" stroke="url(#terms-ellipse-fade-ivory)" d="M35 210A235 88 0 1 0 505 210A235 88 0 1 0 35 210" />
							<use className="terms-ellipse-sweep terms-ellipse-sweep--grand" href="#terms-ellipse-grand" />
							<circle className="terms-ellipse-dot terms-ellipse-dot--large" r="7.5">
								<animateMotion dur="19s" begin="-8s" repeatCount="indefinite">
									<mpath href="#terms-ellipse-grand" />
								</animateMotion>
							</circle>
						</g>

						<g className="terms-ellipse-system terms-ellipse-system--meridian">
							<path id="terms-ellipse-meridian" className="terms-ellipse-line terms-ellipse-line--meridian" stroke="url(#terms-ellipse-fade-meridian)" d="M205 210A65 150 0 1 0 335 210A65 150 0 1 0 205 210" />
							<circle className="terms-ellipse-dot terms-ellipse-dot--small" r="4.2">
								<animateMotion dur="14s" begin="-4s" repeatCount="indefinite">
									<mpath href="#terms-ellipse-meridian" />
								</animateMotion>
							</circle>
						</g>

						<g className="terms-ellipse-node terms-ellipse-node--left">
							<g className="terms-ellipse-system terms-ellipse-system--node-left">
								<path id="terms-ellipse-node-left" className="terms-ellipse-line terms-ellipse-line--node" stroke="url(#terms-ellipse-fade-node-left)" d="M50 210A105 54 0 1 0 260 210A105 54 0 1 0 50 210" />
								<circle className="terms-ellipse-dot terms-ellipse-dot--medium" r="5.5">
									<animateMotion dur="11s" begin="-2s" repeatCount="indefinite">
										<mpath href="#terms-ellipse-node-left" />
									</animateMotion>
								</circle>
							</g>
							<g className="terms-ellipse-system terms-ellipse-system--micro-left">
								<path id="terms-ellipse-micro-left" className="terms-ellipse-line terms-ellipse-line--micro" stroke="url(#terms-ellipse-fade-node-left)" d="M121 210A34 34 0 1 0 189 210A34 34 0 1 0 121 210" />
								<circle className="terms-ellipse-dot terms-ellipse-dot--micro" r="2.8">
									<animateMotion dur="5.8s" begin="-1s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
										<mpath href="#terms-ellipse-micro-left" />
									</animateMotion>
								</circle>
							</g>
							<circle className="terms-ellipse-node-pivot" cx="155" cy="210" r="3.5" />
						</g>

						<g className="terms-ellipse-node terms-ellipse-node--right">
							<g className="terms-ellipse-system terms-ellipse-system--node-right">
								<path id="terms-ellipse-node-right" className="terms-ellipse-line terms-ellipse-line--node" stroke="url(#terms-ellipse-fade-node-right)" d="M280 210A105 54 0 1 0 490 210A105 54 0 1 0 280 210" />
								<circle className="terms-ellipse-dot terms-ellipse-dot--paper" r="5.5">
									<animateMotion dur="11s" begin="-7.5s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
										<mpath href="#terms-ellipse-node-right" />
									</animateMotion>
								</circle>
							</g>
							<g className="terms-ellipse-system terms-ellipse-system--micro-right">
								<path id="terms-ellipse-micro-right" className="terms-ellipse-line terms-ellipse-line--micro" stroke="url(#terms-ellipse-fade-node-right)" d="M351 210A34 34 0 1 0 419 210A34 34 0 1 0 351 210" />
								<circle className="terms-ellipse-dot terms-ellipse-dot--micro-paper" r="2.8">
									<animateMotion dur="5.8s" begin="-3.5s" repeatCount="indefinite">
										<mpath href="#terms-ellipse-micro-right" />
									</animateMotion>
								</circle>
							</g>
							<circle className="terms-ellipse-node-pivot" cx="385" cy="210" r="3.5" />
						</g>

						<circle className="terms-ellipse-core-glow" cx="270" cy="210" r="28" />
						<circle className="terms-ellipse-core-pulse" cx="270" cy="210" r="18" />
						<polygon className="terms-ellipse-core" points="270,198 279,210 270,222 261,210" />
					</g>
				</svg>
			</div>
		);
	}

	if (variant === 'index-company') {
		return (
			<div className="page-atmosphere page-atmosphere--index-company" aria-hidden="true">
				<svg viewBox="0 0 520 460" role="presentation">
					<g className="index-company-orbit index-company-orbit--one">
						<circle className="index-company-track" cx="260" cy="230" r="184" />
						<circle className="index-company-dot" cx="444" cy="230" r="6" />
					</g>
					<g className="index-company-orbit index-company-orbit--two">
						<circle className="index-company-track" cx="260" cy="230" r="149" />
						<circle className="index-company-dot" cx="260" cy="81" r="5" />
						<circle className="index-company-dot index-company-dot--small" cx="260" cy="379" r="3.5" />
					</g>
					<g className="index-company-orbit index-company-orbit--three">
						<circle className="index-company-track" cx="260" cy="230" r="116" />
						<circle className="index-company-dot" cx="376" cy="230" r="5.5" />
						<circle className="index-company-dot index-company-dot--small" cx="202" cy="330.5" r="3" />
						<circle className="index-company-dot" cx="202" cy="129.5" r="4" />
					</g>
					<g className="index-company-orbit index-company-orbit--four">
						<circle className="index-company-track" cx="260" cy="230" r="82" />
						<circle className="index-company-dot" cx="260" cy="148" r="4.5" />
					</g>
					<g className="index-company-orbit index-company-orbit--five">
						<circle className="index-company-track" cx="260" cy="230" r="48" />
						<circle className="index-company-dot" cx="308" cy="230" r="4" />
						<circle className="index-company-dot index-company-dot--small" cx="212" cy="230" r="2.5" />
					</g>
				</svg>
			</div>
		);
	}

	if (variant === 'index-work') {
		return (
			<div className="page-atmosphere page-atmosphere--index-work" aria-hidden="true">
				<svg viewBox="0 0 500 430" role="presentation">
					<polyline className="index-work-stair" points="42,352 136,352 136,278 236,278 236,194 338,194 338,90 458,90" />
					<path className="index-work-tracer" d="M42 352H136V278H236V194H338V90H458" />
				</svg>
			</div>
		);
	}

	if (variant === 'index-lab') {
		return (
			<div className="page-atmosphere page-atmosphere--index-lab" aria-hidden="true">
				<svg viewBox="0 0 540 500" role="presentation">
					<defs>
						<linearGradient id="index-lab-fade-ivory" gradientUnits="userSpaceOnUse" x1="40" y1="90" x2="500" y2="410">
							<stop offset="0" stopColor="#f3efe6" stopOpacity="0" />
							<stop offset="0.22" stopColor="#f3efe6" stopOpacity="0.75" />
							<stop offset="0.55" stopColor="#b4c657" stopOpacity="0.9" />
							<stop offset="0.85" stopColor="#5f6d21" stopOpacity="0.45" />
							<stop offset="1" stopColor="#5f6d21" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="index-lab-fade-chartreuse" gradientUnits="userSpaceOnUse" x1="490" y1="90" x2="70" y2="420">
							<stop offset="0" stopColor="#5f6d21" stopOpacity="0" />
							<stop offset="0.26" stopColor="#96a841" stopOpacity="0.85" />
							<stop offset="0.65" stopColor="#f3efe6" stopOpacity="0.65" />
							<stop offset="1" stopColor="#f3efe6" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="index-lab-fade-amber" gradientUnits="userSpaceOnUse" x1="100" y1="440" x2="440" y2="60">
							<stop offset="0" stopColor="#f3efe6" stopOpacity="0" />
							<stop offset="0.3" stopColor="#c3d564" stopOpacity="0.78" />
							<stop offset="0.72" stopColor="#7a8b30" stopOpacity="0.88" />
							<stop offset="1" stopColor="#5f6d21" stopOpacity="0" />
						</linearGradient>
						<radialGradient id="index-lab-ellipse-lens" cx="48%" cy="46%" r="62%">
							<stop offset="0" stopColor="#b4c657" stopOpacity="0.22" />
							<stop offset="0.45" stopColor="#5f6d21" stopOpacity="0.08" />
							<stop offset="1" stopColor="#080907" stopOpacity="0" />
						</radialGradient>
					</defs>

					<ellipse className="index-lab-ellipse-lens" cx="270" cy="250" rx="195" ry="110" />

					<g className="index-lab-ellipse-system index-lab-ellipse-system--outer">
						<path id="index-lab-ellipse-outer" className="index-lab-ellipse-line index-lab-ellipse-line--outer" stroke="url(#index-lab-fade-ivory)" d="M35 250A235 98 0 1 0 505 250A235 98 0 1 0 35 250" />
						<use className="index-lab-ellipse-sweep index-lab-ellipse-sweep--primary" href="#index-lab-ellipse-outer" />
						<circle className="index-lab-ellipse-dot index-lab-ellipse-dot--large" r="7.5">
							<animateMotion dur="20s" begin="-9s" repeatCount="indefinite">
								<mpath href="#index-lab-ellipse-outer" />
							</animateMotion>
						</circle>
					</g>

					<g className="index-lab-ellipse-system index-lab-ellipse-system--middle">
						<path id="index-lab-ellipse-middle" className="index-lab-ellipse-line index-lab-ellipse-line--middle" stroke="url(#index-lab-fade-chartreuse)" d="M90 250A180 72 0 1 0 450 250A180 72 0 1 0 90 250" />
						<circle className="index-lab-ellipse-dot index-lab-ellipse-dot--medium" r="5.5">
							<animateMotion dur="13s" begin="-4s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
								<mpath href="#index-lab-ellipse-middle" />
							</animateMotion>
						</circle>
					</g>

					<g className="index-lab-ellipse-system index-lab-ellipse-system--inner">
						<path id="index-lab-ellipse-inner" className="index-lab-ellipse-line index-lab-ellipse-line--inner" stroke="url(#index-lab-fade-amber)" d="M182 250A88 168 0 1 0 358 250A88 168 0 1 0 182 250" />
						<use className="index-lab-ellipse-sweep index-lab-ellipse-sweep--steep" href="#index-lab-ellipse-inner" />
						<circle className="index-lab-ellipse-dot index-lab-ellipse-dot--small" r="4.2">
							<animateMotion dur="15s" begin="-6s" repeatCount="indefinite">
								<mpath href="#index-lab-ellipse-inner" />
							</animateMotion>
						</circle>
					</g>

					<g className="index-lab-ellipse-system index-lab-ellipse-system--calibration">
						<path id="index-lab-ellipse-calibration" className="index-lab-ellipse-line index-lab-ellipse-line--calibration" stroke="url(#index-lab-fade-ivory)" d="M148 250A122 48 0 1 0 392 250A122 48 0 1 0 148 250" />
						<circle className="index-lab-ellipse-dot index-lab-ellipse-dot--paper" r="3.2">
							<animateMotion dur="8.2s" begin="-2s" repeatCount="indefinite">
								<mpath href="#index-lab-ellipse-calibration" />
							</animateMotion>
						</circle>
					</g>

					<g className="index-lab-ellipse-system index-lab-ellipse-system--micro">
						<path id="index-lab-ellipse-micro" className="index-lab-ellipse-line index-lab-ellipse-line--micro" stroke="url(#index-lab-fade-chartreuse)" d="M222 250A48 48 0 1 0 318 250A48 48 0 1 0 222 250" />
						<circle className="index-lab-ellipse-dot index-lab-ellipse-dot--micro" r="2.5">
							<animateMotion dur="5.4s" begin="-1s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
								<mpath href="#index-lab-ellipse-micro" />
							</animateMotion>
						</circle>
					</g>

					<circle className="index-lab-ellipse-core-glow" cx="270" cy="250" r="30" />
					<circle className="index-lab-ellipse-core-pulse" cx="270" cy="250" r="20" />
					<circle className="index-lab-ellipse-core-pulse index-lab-ellipse-core-pulse--wide" cx="270" cy="250" r="13" />
					<polygon className="index-lab-ellipse-core" points="270,238 282,250 270,262 258,250" />
				</svg>
			</div>
		);
	}

	if (variant === 'index-kairos') {
		return (
			<div className="page-atmosphere page-atmosphere--index-kairos" aria-hidden="true">
				<svg viewBox="0 0 620 470" role="presentation">
					<defs>
						<path id="index-kairos-orbit-outer" d="M54 235A256 92 0 1 0 566 235A256 92 0 1 0 54 235" />
						<path id="index-kairos-orbit-middle" d="M108 235A202 132 0 1 0 512 235A202 132 0 1 0 108 235" />
						<path id="index-kairos-orbit-inner" d="M164 235A146 62 0 1 0 456 235A146 62 0 1 0 164 235" />
					</defs>
					<g className="index-kairos-orbit index-kairos-orbit--outer">
						<use className="index-kairos-orbit-line" href="#index-kairos-orbit-outer" />
						<circle className="index-kairos-orbit-dot" r="5">
							<animateMotion dur="13s" begin="-4s" repeatCount="indefinite">
								<mpath href="#index-kairos-orbit-outer" />
							</animateMotion>
						</circle>
					</g>
					<g className="index-kairos-orbit index-kairos-orbit--middle">
						<use className="index-kairos-orbit-line" href="#index-kairos-orbit-middle" />
						<circle className="index-kairos-orbit-dot" r="4.5">
							<animateMotion dur="17s" begin="-9s" repeatCount="indefinite">
								<mpath href="#index-kairos-orbit-middle" />
							</animateMotion>
						</circle>
					</g>
					<g className="index-kairos-orbit index-kairos-orbit--inner">
						<use className="index-kairos-orbit-line" href="#index-kairos-orbit-inner" />
						<circle className="index-kairos-orbit-dot" r="4">
							<animateMotion dur="9s" begin="-2s" repeatCount="indefinite">
								<mpath href="#index-kairos-orbit-inner" />
							</animateMotion>
						</circle>
					</g>
				</svg>
			</div>
		);
	}

	if (variant === 'index-contact') {
		return (
			<div className="page-atmosphere page-atmosphere--index-contact" aria-hidden="true">
				<svg viewBox="0 0 720 520" role="presentation">
					<defs>
						<linearGradient id="index-contact-fade-outer" gradientUnits="userSpaceOnUse" x1="50" y1="100" x2="675" y2="410">
							<stop offset="0" stopColor="#eeeae0" stopOpacity="0" />
							<stop offset="0.25" stopColor="#eeeae0" stopOpacity="0.58" />
							<stop offset="0.56" stopColor="#a7b75a" stopOpacity="0.9" />
							<stop offset="0.84" stopColor="#667327" stopOpacity="0.4" />
							<stop offset="1" stopColor="#667327" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="index-contact-fade-middle" gradientUnits="userSpaceOnUse" x1="655" y1="126" x2="180" y2="390">
							<stop offset="0" stopColor="#667327" stopOpacity="0" />
							<stop offset="0.26" stopColor="#87963c" stopOpacity="0.78" />
							<stop offset="0.66" stopColor="#eeeae0" stopOpacity="0.66" />
							<stop offset="1" stopColor="#eeeae0" stopOpacity="0" />
						</linearGradient>
						<linearGradient id="index-contact-fade-inner" gradientUnits="userSpaceOnUse" x1="350" y1="350" x2="620" y2="164">
							<stop offset="0" stopColor="#eeeae0" stopOpacity="0" />
							<stop offset="0.34" stopColor="#eeeae0" stopOpacity="0.74" />
							<stop offset="0.76" stopColor="#91a044" stopOpacity="0.92" />
							<stop offset="1" stopColor="#667327" stopOpacity="0" />
						</linearGradient>
						<radialGradient id="index-contact-aperture-glow" cx="50%" cy="50%" r="50%">
							<stop offset="0" stopColor="#eeeae0" stopOpacity="0.34" />
							<stop offset="0.3" stopColor="#a7b75a" stopOpacity="0.2" />
							<stop offset="1" stopColor="#667327" stopOpacity="0" />
						</radialGradient>
					</defs>
					<ellipse className="index-contact-aperture-lens" cx="510" cy="260" rx="122" ry="76" />
					<g className="index-contact-aperture index-contact-aperture--outer">
						<path id="index-contact-aperture-outer" className="index-contact-aperture-line" stroke="url(#index-contact-fade-outer)" d="M42 260A318 168 0 1 0 678 260A318 168 0 1 0 42 260" />
						<use className="index-contact-aperture-sweep index-contact-aperture-sweep--outer" href="#index-contact-aperture-outer" />
						<circle className="index-contact-aperture-dot" r="6.5">
							<animateMotion dur="19s" begin="-11s" repeatCount="indefinite">
								<mpath href="#index-contact-aperture-outer" />
							</animateMotion>
						</circle>
					</g>
					<g className="index-contact-aperture index-contact-aperture--middle">
						<path id="index-contact-aperture-middle" className="index-contact-aperture-line index-contact-aperture-line--middle" stroke="url(#index-contact-fade-middle)" d="M202 260A224 112 0 1 0 650 260A224 112 0 1 0 202 260" />
						<use className="index-contact-aperture-sweep index-contact-aperture-sweep--middle" href="#index-contact-aperture-middle" />
						<circle className="index-contact-aperture-dot index-contact-aperture-dot--paper" r="5">
							<animateMotion dur="10s" begin="-4s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
								<mpath href="#index-contact-aperture-middle" />
							</animateMotion>
						</circle>
					</g>
					<g className="index-contact-aperture index-contact-aperture--inner">
						<path id="index-contact-aperture-inner" className="index-contact-aperture-line index-contact-aperture-line--inner" stroke="url(#index-contact-fade-inner)" d="M350 260A132 62 0 1 0 614 260A132 62 0 1 0 350 260" />
						<circle className="index-contact-aperture-dot index-contact-aperture-dot--small" r="3.8">
							<animateMotion dur="7.5s" begin="-2s" repeatCount="indefinite">
								<mpath href="#index-contact-aperture-inner" />
							</animateMotion>
						</circle>
					</g>
					<path className="index-contact-aperture-axis" d="M96 260C250 260 362 260 482 260" />
					<circle className="index-contact-aperture-core-glow" cx="510" cy="260" r="38" />
					<circle className="index-contact-aperture-core-pulse index-contact-aperture-core-pulse--wide" cx="510" cy="260" r="28" />
					<circle className="index-contact-aperture-core-pulse" cx="510" cy="260" r="17" />
					<circle className="index-contact-aperture-core" cx="510" cy="260" r="5" />
				</svg>
			</div>
		);
	}

	return (
		<div className="page-atmosphere page-atmosphere--cookies" aria-hidden="true">
			<svg viewBox="0 0 520 460" role="presentation">
				<defs>
					<linearGradient id="cookie-ellipse-fade-ivory" gradientUnits="userSpaceOnUse" x1="40" y1="80" x2="480" y2="380">
						<stop offset="0" stopColor="#f3efe6" stopOpacity="0" />
						<stop offset="0.22" stopColor="#f3efe6" stopOpacity="0.75" />
						<stop offset="0.55" stopColor="#b4c657" stopOpacity="0.9" />
						<stop offset="0.85" stopColor="#5f6d21" stopOpacity="0.45" />
						<stop offset="1" stopColor="#5f6d21" stopOpacity="0" />
					</linearGradient>
					<linearGradient id="cookie-ellipse-fade-chartreuse" gradientUnits="userSpaceOnUse" x1="470" y1="80" x2="60" y2="390">
						<stop offset="0" stopColor="#5f6d21" stopOpacity="0" />
						<stop offset="0.26" stopColor="#96a841" stopOpacity="0.85" />
						<stop offset="0.65" stopColor="#f3efe6" stopOpacity="0.65" />
						<stop offset="1" stopColor="#f3efe6" stopOpacity="0" />
					</linearGradient>
					<linearGradient id="cookie-ellipse-fade-amber" gradientUnits="userSpaceOnUse" x1="100" y1="420" x2="420" y2="50">
						<stop offset="0" stopColor="#f3efe6" stopOpacity="0" />
						<stop offset="0.3" stopColor="#c3d564" stopOpacity="0.78" />
						<stop offset="0.72" stopColor="#7a8b30" stopOpacity="0.88" />
						<stop offset="1" stopColor="#5f6d21" stopOpacity="0" />
					</linearGradient>
					<radialGradient id="cookie-ellipse-lens" cx="48%" cy="45%" r="62%">
						<stop offset="0" stopColor="#b4c657" stopOpacity="0.22" />
						<stop offset="0.45" stopColor="#5f6d21" stopOpacity="0.08" />
						<stop offset="1" stopColor="#080907" stopOpacity="0" />
					</radialGradient>
				</defs>

				<ellipse className="cookie-ellipse-lens" cx="260" cy="230" rx="190" ry="105" />

				<g className="cookie-ellipse-system cookie-ellipse-system--primary">
					<path id="cookie-ellipse-primary" className="cookie-ellipse-line cookie-ellipse-line--primary" stroke="url(#cookie-ellipse-fade-ivory)" d="M35 230A225 90 0 1 0 485 230A225 90 0 1 0 35 230" />
					<use className="cookie-ellipse-sweep cookie-ellipse-sweep--primary" href="#cookie-ellipse-primary" />
					<circle className="cookie-ellipse-dot cookie-ellipse-dot--large" r="7.2">
						<animateMotion dur="18s" begin="-8s" repeatCount="indefinite">
							<mpath href="#cookie-ellipse-primary" />
						</animateMotion>
					</circle>
				</g>

				<g className="cookie-ellipse-system cookie-ellipse-system--secondary">
					<path id="cookie-ellipse-secondary" className="cookie-ellipse-line cookie-ellipse-line--secondary" stroke="url(#cookie-ellipse-fade-chartreuse)" d="M85 230A175 68 0 1 0 435 230A175 68 0 1 0 85 230" />
					<circle className="cookie-ellipse-dot cookie-ellipse-dot--medium" r="5.2">
						<animateMotion dur="12s" begin="-3.5s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
							<mpath href="#cookie-ellipse-secondary" />
						</animateMotion>
					</circle>
				</g>

				<g className="cookie-ellipse-system cookie-ellipse-system--meridian">
					<path id="cookie-ellipse-meridian" className="cookie-ellipse-line cookie-ellipse-line--meridian" stroke="url(#cookie-ellipse-fade-amber)" d="M170 230A90 170 0 1 0 350 230A90 170 0 1 0 170 230" />
					<use className="cookie-ellipse-sweep cookie-ellipse-sweep--steep" href="#cookie-ellipse-meridian" />
					<circle className="cookie-ellipse-dot cookie-ellipse-dot--small" r="4.0">
						<animateMotion dur="14s" begin="-6s" repeatCount="indefinite">
							<mpath href="#cookie-ellipse-meridian" />
						</animateMotion>
					</circle>
				</g>

				<g className="cookie-ellipse-system cookie-ellipse-system--session">
					<path id="cookie-ellipse-session" className="cookie-ellipse-line cookie-ellipse-line--session" stroke="url(#cookie-ellipse-fade-ivory)" d="M142 230A118 44 0 1 0 378 230A118 44 0 1 0 142 230" />
					<circle className="cookie-ellipse-dot cookie-ellipse-dot--paper" r="3.4">
						<animateMotion dur="7.8s" begin="-2s" repeatCount="indefinite">
							<mpath href="#cookie-ellipse-session" />
						</animateMotion>
					</circle>
				</g>

				<g className="cookie-ellipse-system cookie-ellipse-system--micro">
					<path id="cookie-ellipse-micro" className="cookie-ellipse-line cookie-ellipse-line--micro" stroke="url(#cookie-ellipse-fade-chartreuse)" d="M212 230A48 48 0 1 0 308 230A48 48 0 1 0 212 230" />
					<circle className="cookie-ellipse-dot cookie-ellipse-dot--micro" r="2.5">
						<animateMotion dur="5.2s" begin="-1s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear">
							<mpath href="#cookie-ellipse-micro" />
						</animateMotion>
					</circle>
				</g>

				<circle className="cookie-ellipse-core-glow" cx="260" cy="230" r="30" />
				<circle className="cookie-ellipse-core-pulse" cx="260" cy="230" r="20" />
				<circle className="cookie-ellipse-core-pulse cookie-ellipse-core-pulse--wide" cx="260" cy="230" r="13" />
				<rect className="cookie-ellipse-core" x="252" y="222" width="16" height="16" rx="3" />
			</svg>
		</div>
	);
};
